import React, { useEffect, useState } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import { isDesktopRuntime } from './db';
import { getAllWrites, getFailureHistory } from './queue';
import { drainQueue, retryWrite, onSyncActivity } from './syncEngine';
import { WriteQueueRow, SyncFailureRow } from './db';

const REFRESH_INTERVAL_MS = 10000;

const HANDLER_LABELS: Record<string, string> = {
  'create-patient': 'Nuevo paciente',
  'create-encounter': 'Consulta',
  'add-condition': 'Diagnóstico',
  'add-allergy': 'Alergia',
  'add-family-history': 'Antecedente familiar',
  'add-procedure': 'Procedimiento',
  'add-clinical-impression': 'Evolución',
  'add-medication-statement': 'Medicamento',
  'create-appointment': 'Cita',
  'create-clinic-appointment': 'Cita (clínica)',
};

function describe(row: WriteQueueRow): string {
  return HANDLER_LABELS[row.handler] ?? row.handler;
}

/** True for a 'pending' row that's actually a failure backing off before its next automatic retry (see syncEngine.ts). */
function isBackingOff(row: WriteQueueRow): boolean {
  return row.status === 'pending' && row.attempt_count > 0 && !!row.next_retry_at;
}

function formatFailureTime(iso: string): string {
  return new Date(iso).toLocaleString('es-HN', { dateStyle: 'short', timeStyle: 'medium' });
}

/**
 * Self-contained global widget (mounted once in App.tsx, alongside
 * useSyncEngine()) -- not wired into any specific dashboard layout so it
 * doesn't require touching those files. Hidden entirely when there's
 * nothing pending/failed and the app isn't running in the desktop shell,
 * to stay out of the way for the common case.
 */
export function SyncStatusPanel(): React.ReactElement | null {
  const isOnline = useOnlineStatus();
  const [rows, setRows] = useState<WriteQueueRow[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyOpenFor, setHistoryOpenFor] = useState<string | null>(null);
  const [history, setHistory] = useState<SyncFailureRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const refresh = React.useCallback(() => {
    if (!isDesktopRuntime()) return;
    getAllWrites().then(setRows).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    const unsubscribe = onSyncActivity(refresh);
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [refresh]);

  // Always visible as a collapsed pill in the desktop shell -- this is the
  // user's one persistent "am I connected" indicator, so it must never
  // disappear just because everything happens to be synced right now
  // (that "all clear" state, ironically, is exactly when someone would
  // glance at it to confirm they're online).
  if (!isDesktopRuntime()) return null;

  const pending = rows.filter((r) => r.status === 'pending' || r.status === 'in-flight');
  const failed = rows.filter((r) => r.status === 'failed');

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await drainQueue();
    } finally {
      setSyncing(false);
      refresh();
    }
  };

  const handleToggleHistory = async (clientUuid: string) => {
    if (historyOpenFor === clientUuid) {
      setHistoryOpenFor(null);
      return;
    }
    setHistoryOpenFor(clientUuid);
    setHistoryLoading(true);
    try {
      setHistory(await getFailureHistory(clientUuid));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRetry = async (clientUuid: string) => {
    await retryWrite(clientUuid);
    refresh();
    if (isOnline) handleSyncNow();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 font-sans">
      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium ${
            failed.length > 0
              ? 'bg-red-600 text-white'
              : pending.length > 0
              ? 'bg-amber-500 text-white'
              : isOnline
              ? 'bg-white text-gray-500 border border-gray-200'
              : 'bg-gray-700 text-white'
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
          {failed.length > 0
            ? `${failed.length} sin sincronizar`
            : pending.length > 0
            ? `Sincronizando ${pending.length}...`
            : isOnline
            ? 'En línea'
            : 'Sin conexión'}
        </button>
      ) : (
        <div className="w-80 max-h-96 flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="font-medium text-gray-900 text-sm">{isOnline ? 'En línea' : 'Sin conexión'}</span>
            </div>
            <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">
              &times;
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-500 p-4">Sin actividad reciente.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {rows
                  .filter((r) => r.status !== 'done')
                  .map((row) => (
                    <li key={row.client_uuid} className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{describe(row)}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            row.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : row.status === 'in-flight'
                              ? 'bg-blue-100 text-blue-700'
                              : isBackingOff(row)
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {row.status === 'failed'
                            ? 'Falló'
                            : row.status === 'in-flight'
                            ? 'Sincronizando'
                            : isBackingOff(row)
                            ? 'Reintentando...'
                            : 'Pendiente'}
                        </span>
                      </div>
                      {isBackingOff(row) && (
                        <p className="text-xs text-gray-500 mt-1 break-words">
                          Intento {row.attempt_count} falló, reintentando automáticamente: {row.last_error}
                        </p>
                      )}
                      {row.status === 'failed' && (
                        <>
                          <p className="text-xs text-gray-500 mt-1 break-words">{row.last_error}</p>
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              onClick={() => handleRetry(row.client_uuid)}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800"
                            >
                              Reintentar
                            </button>
                            <button
                              onClick={() => handleToggleHistory(row.client_uuid)}
                              className="text-xs font-medium text-gray-500 hover:text-gray-700"
                            >
                              {historyOpenFor === row.client_uuid ? 'Ocultar historial' : 'Ver historial'}
                            </button>
                          </div>
                          {historyOpenFor === row.client_uuid && (
                            <div className="mt-2 pl-2 border-l-2 border-gray-200 space-y-1">
                              {historyLoading ? (
                                <p className="text-xs text-gray-400">Cargando...</p>
                              ) : history.length === 0 ? (
                                <p className="text-xs text-gray-400">Sin historial registrado.</p>
                              ) : (
                                history.map((h) => (
                                  <div key={h.id} className="text-xs text-gray-500">
                                    <span className="text-gray-400">
                                      Intento {h.attempt_number} · {formatFailureTime(h.occurred_at)}
                                    </span>
                                    <p className="break-words">{h.error}</p>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleSyncNow}
              disabled={!isOnline || syncing}
              className="w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar ahora'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
